import { ChangePOIControl } from "./ChangePOIControl";
import { ChangeStylesControl } from "./ChangeStylesControl";
import { mapConfig, controlPos, isMobile, showStr, showStrNotImp, hideStr } from "./Config";
import { CustomPoint, CustomPointNoCoords } from "./CustomPoint";
import { Geocoder } from "./Geocoder";
import { GetLocationControl } from "./GetLocationControl";
import { Marker } from "./Marker";
import { OverlayCloseMapControl, OverlayMapControl } from "./OverlayMapControl";
import { Route } from "./Route";

export class HeroldMap {
    map;
    key;
    coordinates = [];
    type;
    controls = {
        zoom: "",
        location: "",
        poiview: "",
        styles: "",
        overlay: "",
        fullsize: "",
        popup: "false",
        tracking: "",
    };
    zoom;
    style = mapConfig.basic.style;
    poiconfig;
    features;
    featuresForeignAdvertiser;
    featuresNoFitBounds;
    padding = { top: 0, right: 0, bottom: 0, left: 0 };
    additionalLayers =
        []; /* HERO-1009: workaround for https://github.com/mapbox/mapbox-gl-js/issues/4000 */
    currentPopup = {};
    myLocation;
    geolocation;
    changeStyles;
    changePOIview;
    findPois;

    setKey(key) {
        this.key = key;
        return this;
    }
    setType(type) {
        this.type = type;
        return this;
    }
    setControls(controls) {
        this.controls = controls;
        return this;
    }
    setZoom(zoom) {
        this.zoom = zoom;
        return this;
    }
    setStyle(style) {
        switch (style) {
            case "Luftbilder":
                this.style = mapConfig.layers.filter((obj) => {
                    return obj.name === "Luftbilder";
                })[0].style;
                break;
            case "Hybrid":
                this.style = mapConfig.layers.filter((obj) => {
                    return obj.name === "Hybrid";
                })[0].style;
                break;
            default:
                this.style = mapConfig.basic.style;
                break;
        }
        return this;
    }
    setPoiconfig(poiconfig) {
        this.poiconfig = poiconfig;
        return this;
    }
    setFeatures(features) {
        this.features = features;
        return this;
    }
    setFeaturesAdditional(
        featuresSponsorAdvertiser,
        featuresForeignAdvertiser
    ) {
        this.featuresForeignAdvertiser = featuresForeignAdvertiser;
        this.featuresNoFitBounds = featuresSponsorAdvertiser;

        if (this.features.features.length == 0) {
            this.features = featuresForeignAdvertiser;
        }
        return this;
    }
    build() {
        this.padding = this.type
            ? mapConfig[this.type].padding
            : mapConfig["basic"].padding;

        this.setCoordinates(this.features);

        /**
         * Create Map
         */
        mapboxgl.accessToken = this.key;
        this.map = new mapboxgl.Map({
            style: this.style,
            defaultLanguage: "name_de",
            dragRotate: mapConfig.basic.dragRotate,
            dragPan: mapConfig.basic.dragPan,
            touchZoomRotate: mapConfig.basic.touchZoomRotate,
            trackResize: mapConfig.basic.trackResize,
            attributionControl: mapConfig.basic.attributionControl,
            maxZoom: mapConfig.basic.maxZoom,
            container: mapConfig[this.type].container,
            center: this.getCenter(),
            zoom: this.getInitialZoom(),
            interactive: mapConfig[this.type].interactive,
            scrollZoom: mapConfig[this.type].scrollZoom,
        });
        this.map.touchZoomRotate.disableRotation();

        /**
         * event listener
         */
        this.map.once("load", (e) => {
            this.getPOIs(this.type);
        });
        this.map.on("moveend", (e) => {
            this.getPOIs(this.type);
        });
        this.map.on("style.load", () => {
            /* HERO-1009: workaround for https://github.com/mapbox/mapbox-gl-js/issues/4000 */
            const waiting = () => {
                if (!this.map.isStyleLoaded()) {
                    setTimeout(waiting, 200);
                } else {
                    this.additionalLayers.forEach((item) => {
                        let mapLayer = this.map.getLayer(item.name);
                        if (typeof mapLayer == "undefined") {
                            this.map.addSource(item.name, item.source);
                            this.map.addLayer(item.layer);
                        }
                    });
                }
            };
            waiting();
        });

        /**
         * Overrule Zoom
         */
        this.adjustZoom();

        this.addControls();

        /**
         * markers
         */
        let tempFeatures = this.features.features;
        tempFeatures.reverse();
        tempFeatures.forEach((marker) => {
            marker.properties.newStyle = true;
            new Marker()
                .setType(
                    marker.properties.highlight
                        ? "companyPinHighlight"
                        : "companyPin"
                )
                .setPopup(this.controls.popup)
                .build(this, marker);
        });
        if (this.featuresForeignAdvertiser) {
            tempFeatures = this.featuresForeignAdvertiser.features;
            tempFeatures.reverse();
            tempFeatures.forEach((marker) => {
                marker.properties.newStyle = true;
                new Marker()
                    .setType("companyPin")
                    .setPopup(this.controls.popup)
                    .build(this, marker);
            });
        }
        if (this.featuresNoFitBounds) {
            tempFeatures = this.featuresNoFitBounds.features;
            tempFeatures.reverse();
            tempFeatures.forEach((marker) => {
                marker.properties.newStyle = true;
                new Marker()
                    .setType("companyPinStar")
                    .setPopup(this.controls.popup)
                    .build(this, marker);
            });
        }

        return this;
    }

    addControls() {
        /**
         * Define available interaction
         */
        let attribution = new mapboxgl.AttributionControl();
        let language = new MapboxLanguage({ defaultLanguage: "de" });
        //        {'zoom': '', 'location': '', 'poiview': '', 'styles': '', 'overlay': '', 'close': '', 'fullsize': '', 'tracking': ''};
        if (this.controls.overlay) {
            let overlay = new OverlayMapControl(this.controls.tracking);
            this.map.addControl(overlay, controlPos[this.controls.overlay]);
        }
        if (this.controls.overlayclose) {
            let overlayclose = new OverlayCloseMapControl();
            this.map.addControl(
                overlayclose,
                controlPos[this.controls.overlayclose]
            );
        }
        if (this.controls.fullsize) {
            let fullScreen = new mapboxgl.FullscreenControl();
            this.map.addControl(fullScreen, controlPos[this.controls.fullsize]);
        }
        if (this.controls.styles) {
            this.changeStyles = new ChangeStylesControl();
            this.map.addControl(
                this.changeStyles,
                controlPos[this.controls.styles]
            );
        }
        if (this.controls.poiview) {
            this.changePOIview = new ChangePOIControl(this);
            this.map.addControl(
                this.changePOIview,
                controlPos[this.controls.poiview]
            );
        }
        if (this.controls.location) {
            this.geolocation = new GetLocationControl(this);
            this.map.addControl(
                this.geolocation,
                controlPos[this.controls.location]
            );
        }
        if (this.controls.zoom) {
            let navigation = new mapboxgl.NavigationControl({
                showCompass: false,
            });
            this.map.addControl(navigation, controlPos[this.controls.zoom]);
        }
        /**
         * Enable/Disable basic Interaction
         */
        let attributionPosition = "bottom-left";
        this.map.addControl(attribution, attributionPosition);
        this.map.addControl(language);
        //        this.toggleScroll();
        $("#layers input").removeAttr("checked");
        $('#layers input[data-style="' + this.style + '"]').attr("checked", "");
    }

    checkPredefinedPoints() {
        return false;
    }

    setCoordinates(item) {
        this.coordinates = [];
        item.features.forEach((feature) => {
            this.coordinates.push(feature.geometry.coordinates);
        });

        if (!this.coordinates.length) {
            this.coordinates = mapConfig.boundsOfAT;
        }
    }

    setPin(poiAction, point) {
        let pinId = "";
        if (poiAction !== "location") {
            pinId = poiAction;
        }
        point.properties = {};
        point.properties.id = pinId;
        point.properties.newStyle = true;

        this.features.features.push(point);
        this.setCoordinates(this.features);
        new Marker()
            .setType(pinId == "" ? "locationPin" : "routePin")
            .setPopup(false)
            .build(this, point);
    }

    getInitialZoom() {
        let zoom;
        if (this.zoom && this.zoom !== 0) {
            zoom = this.zoom - 1;
        } else {
            zoom = mapConfig[this.type].zoom;
        }
        return zoom;
    }

    getCenter() {
        let coordinatesLng = [];
        let coordinatesLat = [];
        this.coordinates.forEach((marker) => {
            coordinatesLng.push(marker[0]);
            coordinatesLat.push(marker[1]);
        });
        return [
            Math.min.apply(Math, coordinatesLng) +
                (Math.max.apply(Math, coordinatesLng) -
                    Math.min.apply(Math, coordinatesLng)) /
                    2,
            Math.min.apply(Math, coordinatesLat) +
                (Math.max.apply(Math, coordinatesLat) -
                    Math.min.apply(Math, coordinatesLat)) /
                    2,
        ];
    }

    getBounds() {
        return this.coordinates.reduce(
            (bounds, coord) => {
                return bounds.extend(coord);
            },
            new mapboxgl.LngLatBounds(this.coordinates[0], this.coordinates[0]) // HLM suspicious
        );
    }

    centerMap(lngLat) {
        if (!lngLat) {
            this.map.setCenter(this.getCenter());
        } else {
            this.map.setCenter(lngLat);
        }
    }

    flyMeTo(lngLat, padding, speed, zoom) {
        if (!lngLat) {
            lngLat = this.getCenter();
        }
        if (!padding) {
            padding = [0, 0];
        }
        if (!speed) {
            speed = mapConfig.basic.animateSpeed;
        }
        if (zoom) {
            this.map.flyTo({
                center: lngLat,
                offset: padding,
                speed: speed,
                zoom: zoom,
            });
        } else {
            this.map.flyTo({
                center: lngLat,
                offset: padding,
                speed: speed,
            });
        }
    }

    adjustZoom() {
        if (this.coordinates.length > 1) {
            this.map.fitBounds(this.getBounds(), {
                padding: this.padding,
                duration: mapConfig.basic.animateDuration,
            });
        }
    }

    resize() {
        if (isMobile.matches) {
            let topHeight = 0;
            let bottomHeight = 0;
            if ($("#mobileRouteTop .content").is(":visible")) {
                topHeight += $("#mobileRouteTop .content").height();
            }
            if ($("#mobileRouteBottom .content").is(":visible")) {
                bottomHeight += $("#mobileRouteBottom").height();
            }
            if ($("#mobileCompanyInfo").length) {
                bottomHeight += $("#mobileCompanyInfo").height();
            }
            $("#map").css("top", topHeight);
            $("#map").css("bottom", bottomHeight);
        }
        this.map.resize();
    }

    getPOIs(type) {
        $("#" + mapConfig[type].container + " .poi").addClass("old");
        let params = {
            callback: "geojson",
            minx: this.map.getBounds()._sw.lng,
            miny: this.map.getBounds()._sw.lat,
            maxx: this.map.getBounds()._ne.lng,
            maxy: this.map.getBounds()._ne.lat,
            z: Math.round(this.map.getZoom() + 1),
            type: "",
        };

        params.type = (() => {
            let pois = [];
            this.poiconfig.forEach((poi) => {
                if (poi.defaultsel) {
                    pois.push(poi.id);
                }
            });
            return pois;
        })().join(",");

        // no ajax call if no poi group is selected
        if (params.type === "") {
            return;
        }

        if (this.findPois && this.findPois.readyState < 4) {
            this.findPois.abort();
        }

        this.findPois = $.ajax({
            type: "GET",
            data: params,
            url: "/servlet/findPOIs",
            context: this,
            dataType: "JSON",
            success: (response) => {
                response.geojson.features.forEach((marker) => {
                    new Marker().setType("poi").build(this, marker);
                });
            },
            complete: () => {
                $("#" + mapConfig[type].container + " .poi.old").remove();
            },
        });
    }
}
export class HeroldMapRoute extends HeroldMap {
    enableLocation = true;
    enablePOIview = true;
    route = {
        origin: {},
        destination: {},
        waypoints: [],
    };
    lastReverseSearch = {};
    searchMarker;
    geocoderHBD = new Geocoder();
    planRoute = new Route();

    build(routePoints) {
        super.build();

        this.planRoute.setMap(this);
        this.planRoute.build();

        if (routePoints.length >= 2) {
            this.checkPredefinedPoints();
            this.checkRouteComplete();
        } else {
            this.geolocation.getLocation(this);
        }

        this.map.on("click", (e) => {
            this.clickEvent(e);
        });

        this.map.on("contextmenu", (e) => {
            this.clickEvent(e);
        });

        /**
         * Toggle Top-Overlay (mobile only)
         */
        $("#mobileRouteTop .toggle .icon").click((e) => {
            $("#mobileRouteTop .content").toggle();
            $("#mobileRouteTop .toggle .icon").toggleClass(
                "icon-arrow_up icon-arrow_down"
            );
            this.resize();
        });


        return this;
    }

    setControls() {
        this.controls = {
            zoom: "br",
            location: "br",
            poiview: "br",
            styles: "br",
            fullsize: "br",
        };
        return this;
    }

    checkPredefinedPoints() {
        let hasPoints = false;
        if (routePoints.length) {
            hasPoints = true;
            let predefinedPoint;

            routePoints.forEach((routePoint) => {
                let schema = {
                    start: "origin",
                    end: "destination",
                    via: "waypoint0",
                };
                let type = schema[routePoint.type];

                if (type == "waypoint0") {
                    $(".addStop").click();
                }

                $("#" + type).val(routePoint.name);

                if (!routePoint.coordinates) {
                    predefinedPoint = new CustomPointNoCoords()
                        .setId(type)
                        .setName(routePoint.name)
                        .build();
                } else {
                    predefinedPoint = new CustomPoint()
                        .setId(type)
                        .setName(routePoint.name)
                        .setCoordinates(routePoint.coordinates)
                        .build();
                }
                predefinedPoint.properties.newStyle = true;
                new Marker()
                    .setType("routePin")
                    .setPopup(false)
                    .build(this, predefinedPoint);

                this.flyMeTo(predefinedPoint.geometry.coordinates, null, 5, 18);

                this.route[type] = predefinedPoint;
            });
        }
        return hasPoints;
    }
    clickEvent(e) {
        e.preventDefault();
        this.changePOIview.hideMe();
        this.changeStyles.hideMe();
        if ($("#reverseSearchMarker").length || $(".mapboxgl-popup").length) {
            $("#reverseSearchMarker").remove();
            $(".mapboxgl-popup").remove();
        } else {
            if (
                e.originalEvent.target.nodeName == "CANVAS" ||
                !$(e.originalEvent.target).data("hasPopup")
            ) {
                this.reverseSearch(e);
            }
        }
    }

    clearMap(options) {
        if (options.route) {
            this.additionalLayers.forEach((item) => {
                /* HERO-1009: workaround for https://github.com/mapbox/mapbox-gl-js/issues/4000 */
                this.map.removeLayer(item.name);
                this.map.removeSource(item.name);
            });
            this.additionalLayers =
                []; /* HERO-1009: workaround for https://github.com/mapbox/mapbox-gl-js/issues/4000 */
            $(".routeOverview").empty();
            $(".routeDesc").empty();
        }
        if (options.pins) {
            $(".mapboxgl-popup-content").remove();
            $('[class^="mapboxgl-ctrl-bottom"]').css("bottom", 0);
        }
        if (options.routepins) {
            $(".hbdpin").remove();
        } else {
            $('.hbdpin:not([id^="route"])').remove();
        }
    }

    reverseSearch(eventData) {
        this.geocoderHBD.reverseQuery(eventData.lngLat, (data) => {
            this.searchMarker = data.features[0];
            this.features.features.push(data.features[0]);
            this.searchMarker.id = "reversesearchMarker";
            let markerObject = new Marker()
                .setType("reverseSearchMarker")
                .setOffset(5)
                .setPopup(true)
                .build(this, this.searchMarker);
            this.lastReverseSearch = this.searchMarker;
            markerObject.togglePopup();
        });
    }

    checkRouteComplete() {
        let knownPoints =
            (this.route.origin.properties ? 2 : 0) +
            (this.route.destination.properties ? 2 : 0) +
            (this.myLocation ? 1 : 0);
        if (knownPoints >= 3) {
            if (knownPoints == 3) {
                if (!this.route.origin.properties) {
                    this.route.origin = this.myLocation;
                    this.setPin("origin", this.myLocation);
                }
                if (!this.route.destination.properties) {
                    this.route.destination = this.myLocation;
                    this.setPin("destination", this.myLocation);
                }
            }
            this.planRoute.getRoute();
        }
        if (knownPoints >= 2) {
            let start = this.route.origin;
            let end = this.route.destination;

            $("input#origin").val(start.place_name);
            $("input#destination").val(end.place_name);

            this.clearMap({ route: true, pins: true, routepins: false });

            if (!isMobile.matches && $("#routebar").is(":hidden")) {
                $(".sidebar").attr("style", hideStr);
                $("#routebar").attr("style", showStr);
                $("button.close").attr("style", showStrNotImp);
            }
            if (isMobile.matches) {
                $(".sidebar").attr("style", hideStr);
                if (!jQuery.isEmptyObject(this.currentPopup)) {
                    this.currentPopup.remove();
                    this.currentPopup = {};
                }
                $("#form-search-route").prependTo("#mobileRouteTop .content");
                $("#mobileRouteTop").attr("style", showStr);
                this.resize();
            }
        }
    }
}
